import React from 'react';
import { FileUp } from 'lucide-react';
import { Input } from '../../../../../../../components/ui/input';
import { Label } from '../../../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../../../components/ui/select';
import type { CoatingChecklistTemplateSection } from '../../../specifications/Coating/coatingChecklistMockData';

type ChecklistItem = CoatingChecklistTemplateSection['items'][number];
type ChecklistTableData = NonNullable<ChecklistItem['tableData']>;

const EMPTY_TABLE: ChecklistTableData = {
  columns: ['Value'],
  rows: [{ name: 'Row 1', values: [''], unit: '', comparator: '=', minValue: '', maxValue: '' }],
};

function tableData(item: ChecklistItem): ChecklistTableData {
  if (item.tableData?.rows?.length && item.tableData.columns?.length) return item.tableData;
  return EMPTY_TABLE;
}

function tableValueKey(itemId: string, rowIndex: number, columnIndex: number) {
  return `table::${itemId}::${rowIndex}::${columnIndex}`;
}

function fileValueKey(itemId: string) {
  return `file::${itemId}`;
}

function criterionLabel(criteria: string | undefined) {
  return criteria === 'Dateandtime' ? 'Date time' : criteria || 'Accept/Reject/NA';
}

function renderMeta(item: ChecklistItem) {
  const parts = [item.comparator, item.value, item.unit].filter((part) => part != null && String(part).trim() !== '' && part !== 'N/A');
  if (parts.length === 0) return null;
  return <span className="text-xs text-slate-500">{parts.join(' ')}</span>;
}

function ChecklistValueInput({
  item,
  values,
  onValueChange,
}: {
  item: ChecklistItem;
  values: Record<string, string>;
  onValueChange: (key: string, value: string) => void;
}) {
  const criteria = item.criteria || 'Accept/Reject/NA';
  const value = values[item.id] ?? '';

  if (criteria === 'Accept/Reject/NA') {
    return (
      <Select value={value || 'N/A'} onValueChange={(next) => onValueChange(item.id, next)}>
        <SelectTrigger className="h-9 bg-white text-sm">
          <SelectValue placeholder="Select result" />
        </SelectTrigger>
        <SelectContent className="z-[9999]">
          <SelectItem value="Accept">Accept</SelectItem>
          <SelectItem value="Reject">Reject</SelectItem>
          <SelectItem value="N/A">N/A</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  if (criteria === 'Dateandtime') {
    return (
      <Input
        type="datetime-local"
        className="h-9 text-sm"
        value={value}
        onChange={(event) => onValueChange(item.id, event.target.value)}
      />
    );
  }

  if (criteria === 'Table') {
    const table = tableData(item);
    return (
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full min-w-[620px] border-collapse text-xs">
          <thead>
            <tr className="bg-slate-50">
              <th className="w-[160px] border border-slate-200 px-2 py-2 text-left font-medium text-slate-700">
                Row
              </th>
              {table.columns.map((column, columnIndex) => (
                <th key={`${column}-${columnIndex}`} className="border border-slate-200 px-2 py-2 text-left font-medium text-slate-700">
                  {column || `Column ${columnIndex + 1}`}
                </th>
              ))}
              <th className="w-[120px] border border-slate-200 px-2 py-2 text-left font-medium text-slate-700">
                Unit
              </th>
              <th className="w-[120px] border border-slate-200 px-2 py-2 text-left font-medium text-slate-700">
                Comparator
              </th>
              <th className="w-[100px] border border-slate-200 px-2 py-2 text-left font-medium text-slate-700">
                Min
              </th>
              <th className="w-[100px] border border-slate-200 px-2 py-2 text-left font-medium text-slate-700">
                Max
              </th>
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr key={`${row.name}-${rowIndex}`}>
                <td className="border border-slate-200 px-2 py-2 font-medium text-slate-700">
                  {row.name || `Row ${rowIndex + 1}`}
                </td>
                {table.columns.map((_, columnIndex) => (
                  <td key={columnIndex} className="border border-slate-200 p-1.5">
                    <Input
                      className="h-8 text-xs"
                      value={values[tableValueKey(item.id, rowIndex, columnIndex)] ?? ''}
                      onChange={(event) => onValueChange(tableValueKey(item.id, rowIndex, columnIndex), event.target.value)}
                    />
                  </td>
                ))}
                <td className="border border-slate-200 px-2 py-2 text-slate-600">{row.unit || item.unit || 'N/A'}</td>
                <td className="border border-slate-200 px-2 py-2 text-slate-600">{row.comparator || '-'}</td>
                <td className="border border-slate-200 px-2 py-2 text-slate-600">{row.minValue || '-'}</td>
                <td className="border border-slate-200 px-2 py-2 text-slate-600">{row.maxValue || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <Input
      className="h-9 text-sm"
      value={value}
      onChange={(event) => onValueChange(item.id, event.target.value)}
      placeholder={criteria === 'Text' ? 'Enter text' : 'Enter value'}
    />
  );
}

export function ProductionChecklistCards({
  sections,
  values,
  noteValues,
  onValueChange,
  onNoteChange,
}: {
  sections: CoatingChecklistTemplateSection[];
  values: Record<string, string>;
  noteValues?: Record<string, string>;
  onValueChange: (key: string, value: string) => void;
  onNoteChange?: (key: string, value: string) => void;
}) {
  return (
    <div className="space-y-3">
      {sections.map((section, sectionIndex) => (
        <div key={section.id} className="overflow-visible rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="rounded-t-lg border-b border-slate-200 bg-slate-50 px-3 py-2">
            <h4 className="m-0 text-sm font-semibold text-slate-900">
              {sectionIndex + 1}. {section.category}
            </h4>
          </div>
          <div className="divide-y divide-slate-100">
            {section.items.length === 0 ? (
              <p className="m-0 px-3 py-3 text-sm text-slate-500">No checklist items in this category.</p>
            ) : null}
            {section.items.map((item, itemIndex) => (
              <div key={item.id} className="grid grid-cols-1 gap-3 px-3 py-3 lg:grid-cols-[minmax(0,1fr)_minmax(280px,1.1fr)]">
                <div className="min-w-0 space-y-1">
                  <Label className="text-sm font-medium text-slate-800">
                    {itemIndex + 1}. {item.description || 'Checklist item'}
                  </Label>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                      {criterionLabel(item.criteria)}
                    </span> */}
                    {/* {renderMeta(item)} */}
                    {/* {item.checkboxRequired ? (
                      <span className="rounded bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">Required</span>
                    ) : null} */}
                    {item.fileUploadRequired ? (
                      <span className="inline-flex items-center gap-1 rounded bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-800">
                        <FileUp className="h-3 w-3" />
                        File/Image required
                      </span>
                    ) : null}
                  </div>
                  {item.suffix ? <p className="m-0 text-xs text-slate-500">{item.suffix}</p> : null}
                </div>
                <div className="min-w-0 space-y-2">
                  <ChecklistValueInput item={item} values={values} onValueChange={onValueChange} />
                  {item.fileUploadRequired ? (
                    <Input
                      type="file"
                      className="h-9 text-sm"
                      onChange={(event) =>
                        onValueChange(
                          fileValueKey(item.id),
                          Array.from(event.target.files ?? []).map((file) => file.name).join(', '),
                        )
                      }
                    />
                  ) : null}
                  {onNoteChange ? (
                    <Input
                      className="h-9 text-sm"
                      value={noteValues?.[item.id] ?? ''}
                      onChange={(event) => onNoteChange(item.id, event.target.value)}
                      placeholder="Add notes"
                    />
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
